/**
 * PlanNode - Visual display of AI agent's plan with real-time progress
 *
 * Features:
 * - Checklist of plan steps with status icons
 * - Current task highlighted with visual indicator
 * - ThinkingIndicator for streaming AI thoughts
 * - Click-to-rollback support (for future implementation)
 *
 * This component enables users to see the AI's planning process
 * and track execution progress in real-time on the canvas.
 *
 * Data updates come via Yjs from the Board Durable Object when
 * the planning middleware executes.
 */
import { memo } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, type Node } from '@xyflow/react';
import type { PlanStep } from '@deep-agents/shared-types';
import { ThinkingIndicator } from './ThinkingIndicator';

// ============================================================================
// NODE DATA TYPES
// ============================================================================

/**
 * PlanNodeData - Data shape stored in Y.Map for plan nodes
 *
 * @property tasks - Array of plan steps from the planning middleware
 * @property currentTaskId - ID of the task currently being executed
 * @property streamingThoughts - Live "thinking" text from the AI
 * @property title - Optional title for the plan (defaults to "AI Plan")
 *
 * Note: Index signature required for React Flow Node<T> type compatibility
 */
export interface PlanNodeData {
  /** The plan steps generated by the AI */
  tasks: PlanStep[];

  /** ID of the currently executing task (for highlighting) */
  currentTaskId?: string;

  /** Streaming thoughts from the AI (optional) */
  streamingThoughts?: string;

  /** Plan title for display */
  title?: string;

  // Index signature for React Flow compatibility
  [key: string]: unknown;
}

/**
 * PlanNodeType - Full node type for type-safe React Flow usage
 *
 * Use this type when working with plan nodes in React Flow:
 * - In nodeTypes registry
 * - When creating new plan nodes
 * - In onNodesChange handlers
 */
export type PlanNodeType = Node<PlanNodeData, 'plan'>;

// ============================================================================
// STATUS ICON COMPONENT
// ============================================================================

/**
 * StatusIcon - Visual indicator for plan step status
 *
 * Renders different icons based on step status:
 * - completed: Green checkmark (✓)
 * - in_progress: Spinning loader
 * - failed: Red X
 * - pending: Gray circle
 */
function StatusIcon({ status }: { status: PlanStep['status'] }) {
  switch (status) {
    case 'completed':
      return (
        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      );

    case 'in_progress':
      return (
        <svg className="w-5 h-5 text-violet-500 animate-spin" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      );

    case 'failed':
      return (
        <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      );

    case 'pending':
    default:
      return (
        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <circle cx="12" cy="12" r="8" strokeWidth="2" />
        </svg>
      );
  }
}

// ============================================================================
// PLAN NODE COMPONENT
// ============================================================================

/**
 * PlanNode - The plan visualization component
 *
 * Rendering behavior:
 * - Header shows plan title with violet accent (AI work theme)
 * - ThinkingIndicator appears when streamingThoughts is present
 * - Each task renders as a checklist item with status icon
 * - Current task highlighted with bg-violet-50
 * - Tasks are clickable (for future rollback integration)
 *
 * Visual hierarchy:
 * - Violet border matches AI/agent workflow theme
 * - Status icons provide quick visual progress scan
 * - Current task highlight draws attention to active step
 */
export const PlanNode = memo(({ id, data, selected }: NodeProps<PlanNodeType>) => {
  const { tasks = [], currentTaskId, streamingThoughts, title = 'AI Plan' } = data;

  // ---------------------------------------------------------------------------
  // TASK CLICK HANDLER (for future rollback)
  // ---------------------------------------------------------------------------

  /**
   * Handle task click - placeholder for future rollback functionality
   *
   * Future implementation will:
   * 1. Identify clicked task and its checkpoint
   * 2. Send rollback request to Board DO
   * 3. Reset agent state to that checkpoint
   * 4. Update plan visualization
   */
  const handleTaskClick = (task: PlanStep) => {
    // TODO: Implement rollback in future plan
    console.log('Task clicked (rollback not yet implemented):', task.id);
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* ===================================================================
          RESIZE HANDLES
          NodeResizer provides drag-to-resize functionality.
          Only visible when the node is selected.
          =================================================================== */}
      <NodeResizer
        minWidth={280}
        minHeight={150}
        isVisible={selected}
        lineClassName="border-violet-500"
        handleClassName="bg-violet-500 border-2 border-white rounded"
      />

      {/* ===================================================================
          CONNECTION HANDLES
          Handles allow connecting plan nodes to other workflow nodes.
          Output handle could feed results to downstream nodes.
          =================================================================== */}
      <Handle type="target" position={Position.Top} id="top" className="w-3 h-3" />
      <Handle type="source" position={Position.Bottom} id="bottom" className="w-3 h-3" />

      {/* ===================================================================
          PLAN NODE CONTENT
          Visual plan display with header, thinking indicator, and checklist.
          =================================================================== */}
      <div
        className="w-full h-full rounded-lg shadow-md overflow-hidden bg-white"
        style={{
          border: selected ? '2px solid var(--color-accent)' : '2px solid #c4b5fd', // Violet border
          minWidth: 280,
          minHeight: 150,
        }}
      >
        {/* -------------------------------------------------------------------
            HEADER - Plan title with violet background
            Violet color indicates AI/agent work (matches Kit color scheme)
            ------------------------------------------------------------------- */}
        <div
          className="px-4 py-3 font-medium text-sm border-b"
          style={{ backgroundColor: 'var(--color-accent)', color: 'white' }}
        >
          {title}
        </div>

        {/* -------------------------------------------------------------------
            BODY - Thinking indicator + task checklist
            Scrollable to handle long plans
            ------------------------------------------------------------------- */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: '400px' }}>
          {/* Thinking indicator - shows at top when AI is thinking */}
          {streamingThoughts && (
            <div className="mb-3">
              <ThinkingIndicator thoughts={streamingThoughts} />
            </div>
          )}

          {/* Task checklist */}
          {tasks.length > 0 ? (
            <div className="space-y-2">
              {tasks.map((task) => {
                const isCurrent = task.id === currentTaskId;

                return (
                  <button
                    key={task.id}
                    onClick={() => handleTaskClick(task)}
                    className={`
                      w-full flex items-start gap-3 p-3 rounded-md text-left
                      transition-colors cursor-pointer
                      ${isCurrent ? 'bg-violet-50 border-2 border-violet-200' : 'bg-gray-50 border-2 border-transparent hover:border-gray-200'}
                    `}
                    title="Click to rollback to this step (coming soon)"
                  >
                    {/* Status icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      <StatusIcon status={task.status} />
                    </div>

                    {/* Task description and metadata */}
                    <div className="flex-1 min-w-0">
                      {/* Description */}
                      <p className="text-sm text-gray-800 break-words">
                        {task.description}
                      </p>

                      {/* Optional metadata (estimated duration, dependencies) */}
                      {(task.estimatedDuration || task.dependsOn) && (
                        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                          {task.estimatedDuration && (
                            <span className="inline-flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {task.estimatedDuration}
                            </span>
                          )}
                          {task.dependsOn && task.dependsOn.length > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              Depends on {task.dependsOn.length} task{task.dependsOn.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            /* Empty state - no tasks yet */
            <div className="text-center py-8 text-gray-400 text-sm">
              <p>No plan generated yet</p>
              <p className="text-xs mt-1">AI will create a plan when given a task</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
});

// Display name for React DevTools debugging
PlanNode.displayName = 'PlanNode';
